import { memo, useCallback, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import {
  useParametricSelector,
  useProfileSelector,
} from "../../../hooks/useParametricSelector";
import { getLoadedUserByUsername } from "../../../redux/slices/dataSlice";
import { useSelector, useDispatch } from "react-redux";
import LoadingScreen from "../../LoadingScreen";
import {
  LoginState,
  accountActions,
  getAccountState,
} from "../../../redux/slices/accountSlice";
import MarkitSecuredContainer from "../../../components/UserInfoContainers/MarkitSecuredContainer";
import { useTheme } from "../../../hooks/useTheme";
import {
  Follower,
  MembershipPlan,
  NotificationType,
} from "@markit/common.types";
import SubscribeFlowMembership from "../../../components/ProfilePage/SubscribeFlow/SubscribeFlowMembership";
import SubscribeFlowMembershipManage from "../../../components/ProfilePage/SubscribeFlow/SubscribeFlowMembershipManage";
import SubscribeFlowMembershipCheckout from "../../../components/ProfilePage/SubscribeFlow/SubscribeFlowMembershipCheckout";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import { CircularProgress } from "@mui/material";
import { Colors } from "../../../utils/colors";
import { API } from "../../../API";
import { useNavigate } from "react-router-dom";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import ConfirmDeleteModal from "../../../components/Containers/ConfirmPopups/ConfirmDeleteModal";
import { Icon } from "@iconify/react";
import { getUserFollowerRef } from "../../../utils/FirebaseUtils";
import { onSnapshot } from "../../../firebase";
import { NavigationId } from "../../../navigation/AppParamList";
import { LoginStage } from "../../Login/LoginWelcome";
import { useLoadUserByUsername } from "../../../hooks/useLoadUserByUsername";
import { onProfilePageNavigatePath } from "../../../utils/navigationUtils";
import { checkIfFollowerIsSubscribed } from "../../../utils/userUtils";
import { testStripe } from "../../../utils/stripeAccountUtils";

export enum SubscribeManageStage {
  MEMBERSHIP = "Membership",
  MEMBERSHIP_PLANS = "Membership Plans",
  MEMBERSHIP_CHECKOUT = "Membership Checkout",
}

const ProfilePageSubscribeManage = memo(
  function ProfilePageSubscribeManageFn() {
    const { accountData, loggedIn, appInitialized } =
      useSelector(getAccountState).account;
    const { theme } = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [selectedMembershipPlan, setSelectedMembershipPlan] =
      useState<MembershipPlan>();
    const [subscribingStage, setSubscribingStage] =
      useState<SubscribeManageStage>(SubscribeManageStage.MEMBERSHIP);
    const [followerData, setFollowerData] = useState<Follower>();
    const [loadingFollower, setLoadingFollower] = useState(true);
    const [confirmCancelModal, setConfirmCancelModal] = useState(false);
    const [processing, setProcessing] = useState(false);

    const username = useProfileSelector();
    useLoadUserByUsername(username);

    const {
      isLoading: isLoadingUserData,
      data: creatorData,
      isError: isErrorUserData,
    } = useParametricSelector(getLoadedUserByUsername, username);

    const isLoggedIn = useMemo(
      () => loggedIn === LoginState.LOGGED_IN,
      [loggedIn]
    );

    // show all non-hidden membership plans. Also, still show if it's your current membership plan
    const visibleMembershipPlans = useMemo(
      () =>
        creatorData
          ? creatorData.membership.membershipPlans.filter(
              (plan) =>
                !plan.hidden || plan.id === followerData?.membershipPlanId
            )
          : [],
      [creatorData, followerData?.membershipPlanId]
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useAsyncEffect(async () => {
      if (!isLoggedIn) {
        dispatch(accountActions.setRedirectPath(location.pathname));
        navigate(NavigationId.SIGN_IN, {
          state: { preLoginStage: LoginStage.PHONE },
        });
        return;
      }
      if (!appInitialized || !creatorData) {
        return;
      }

      // default to a free plan if the user is currently on a paid plan and the free plan is visible, otherwise default to first visible paid plan
      const defaultPlan = visibleMembershipPlans.find((plan) => {
        if (plan.id === "free" && followerData?.membershipPlanId !== "free") {
          return true;
        }
        return plan.id !== "free" && followerData?.membershipPlanId === "free";
      });

      if (defaultPlan) {
        setSelectedMembershipPlan(defaultPlan);
      }

      const followerRef = getUserFollowerRef(creatorData.uid, accountData.uid);
      const unsubscribe = onSnapshot(followerRef, async (snapshot) => {
        const followerData = snapshot.data();
        if (followerData && checkIfFollowerIsSubscribed(followerData.status)) {
          setFollowerData(followerData);
          setLoadingFollower(false);
        } else {
          // If no follower data is found, wait 3 seconds before redirecting
          setLoadingFollower(false);
          setTimeout(() => {
            if (
              !followerData ||
              !checkIfFollowerIsSubscribed(followerData.status)
            ) {
              onProfilePageNavigatePath(
                navigate,
                creatorData.username,
                "subscribe"
              );
            }
          }, 3000);
        }
      });

      return () => {
        unsubscribe();
      };
    }, [
      accountData.uid,
      appInitialized,
      creatorData,
      dispatch,
      followerData?.membershipPlanId,
      isLoggedIn,
      loggedIn,
      navigate,
      visibleMembershipPlans,
    ]);

    const chooseMembershipOnPress = useCallback(
      async (selectedMembership: MembershipPlan) => {
        if (!creatorData) {
          return;
        }
        setSelectedMembershipPlan(selectedMembership);
        if (selectedMembership.id === "free") {
          setConfirmCancelModal(true);
        } else {
          setSubscribingStage(SubscribeManageStage.MEMBERSHIP_CHECKOUT);
        }
      },
      [creatorData]
    );

    const cancelFollowerSubscription = useCallback(async () => {
      if (!creatorData) {
        return;
      }
      setProcessing(true);
      await API.user.cancelFollowerSubscription(accountData.uid, {
        uid: accountData.uid,
        creatorUserId: creatorData.uid,
        creatorStripeAccountId:
          testStripe(creatorData.uid) && creatorData.testStripeAccountId
            ? creatorData.testStripeAccountId
            : creatorData.stripeAccountId,
      });
      showNotificationBanner(dispatch, "Cancelled", NotificationType.NEGATIVE);
      setProcessing(false);
    }, [accountData.uid, creatorData, dispatch]);

    if (
      isLoadingUserData ||
      (loggedIn === LoginState.LOGGED_IN && !appInitialized) ||
      !creatorData
    ) {
      return (
        <LoadingScreen
          isLoadingPage={
            isLoadingUserData ||
            (loggedIn === LoginState.LOGGED_IN && !appInitialized) ||
            !creatorData
          }
          error={isErrorUserData}
        />
      );
    }

    const title = `${creatorData.fullName} (@${creatorData.username}) on Markit`;

    return (
      <>
        <Helmet>
          <title>{title}</title>
          <meta name="og:title" content={title} />
          <meta name="og:description" content={creatorData.username} />
        </Helmet>
        <div
          className="ColumnNormalSpaced"
          style={{ ...theme.SecondaryBG, minHeight: "100vh", paddingTop: 70 }}
        >
          {loadingFollower || !followerData ? (
            <div className="Centering" style={{ marginBlock: "auto" }}>
              <CircularProgress size={24} style={{ color: Colors.GRAY1 }} />
            </div>
          ) : (
            <>
              {subscribingStage === SubscribeManageStage.MEMBERSHIP ? (
                <SubscribeFlowMembershipManage
                  creatorData={creatorData}
                  setConfirmCancelModal={setConfirmCancelModal}
                  setSubscribeStage={setSubscribingStage}
                  followerData={followerData}
                />
              ) : subscribingStage === SubscribeManageStage.MEMBERSHIP_PLANS ? (
                <SubscribeFlowMembership
                  creatorData={creatorData}
                  chooseOnPress={chooseMembershipOnPress}
                  selectedMembershipPlan={selectedMembershipPlan}
                  setSelectedMembershipPlan={setSelectedMembershipPlan}
                  visibleMembershipPlans={visibleMembershipPlans}
                  followerData={followerData}
                />
              ) : subscribingStage ===
                  SubscribeManageStage.MEMBERSHIP_CHECKOUT &&
                selectedMembershipPlan ? (
                <SubscribeFlowMembershipCheckout
                  creatorData={creatorData}
                  selectedMembershipPlan={selectedMembershipPlan}
                  uid={accountData.uid}
                  backOnPress={() =>
                    setSubscribingStage(SubscribeManageStage.MEMBERSHIP_PLANS)
                  }
                />
              ) : null}
            </>
          )}
          <div
            className="Centering"
            style={{ paddingTop: 100, paddingBottom: 30 }}
          >
            <MarkitSecuredContainer />
          </div>
        </div>
        <ConfirmDeleteModal
          heading={"Cancel Paid Subscription?"}
          subtext="Upon cancellation you will lose access to all plan benefits, and your account will be charged for the remainder of your plan's billing cycle. You can resubscribe at any time."
          deleteButtonText="Cancel Subscription"
          hideModal={!confirmCancelModal}
          setIsVisible={setConfirmCancelModal}
          deleteOnPress={cancelFollowerSubscription}
          buttonLoading={processing}
          icon={
            <Icon
              icon={"ion:ribbon"}
              height={35}
              style={{ color: Colors.BLACK }}
            />
          }
        />
      </>
    );
  }
);

export default ProfilePageSubscribeManage;
